import { Badge, Col, Row } from "antd";
import { FC } from "react";
import { Flex } from "src/components/Flex";
import * as Styled from "./CustomizeSmartfolio.css";
import Text from "antd/lib/typography/Text";
import { subCardStyle } from "src/pages/SmartfolioAnalytics/components";
import {
  CancelCircleIcon,
  PlusSignCircleIcon,
} from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { Button as UIButton } from "src/components/ui";
import Title from "antd/lib/typography/Title";
import {
  PieChart,
  PieColors,
} from "src/pages/SmartfolioAnalytics/components/PieChart";
import { useDispatch, useSelector } from "react-redux";
import {
  addBasketItem,
  removeBasketItem,
  selectBasket,
  selectCashWeight,
  selectDescription,
  selectName,
  setDescription,
  setName,
  updateBasketInfo,
  updateBasketItem,
} from "src/store/Goal-Based/SelfMadeSmartfolioSlice";
import { useGetSecuritiesQuery } from "src/store/Goal-Based/goalBasedApi";
import { theme } from "src/theme/theme";

export interface ICustomizeSmartfolio {
  setSecurities: React.Dispatch<React.SetStateAction<any[]>>;
  securities: any[];
}

export const CustomizeSmartfolio: FC = () => {
  const dispatch = useDispatch<any>();
  const { data: tickers } = useGetSecuritiesQuery();
  const basket = useSelector(selectBasket);
  const cashWeight = useSelector(selectCashWeight);
  const name = useSelector(selectName);
  const description = useSelector(selectDescription);

  /* Cash included: the weights are shares of the whole basket, and cash is
     part of it — the first row of the table is cash for exactly that
     reason. */
  const weightsTotal = basket.reduce(
    (sum, item) => sum + (Number(item.weight) || 0) * 100,
    0,
  );

  /**
   * What the Build button is waiting for.
   *
   * The same conditions the button itself checks, said in words. They were
   * only ever expressed as a `disabled` prop.
   */
  const outstanding = [
    basket.length < 2 ? "pick at least 2 securities" : null,
    name.trim().length < 5 ? "give it a name of 5 characters or more" : null,
    description.trim().length < 5
      ? "write a description of 5 characters or more"
      : null,
    basket.slice(1).some((item) => Number(item.weight) <= 0)
      ? "give every security a weight above 0%"
      : null,
  ].filter(Boolean) as string[];

  return (
    <Row gutter={[16, 16]}>
      <Col span={24} lg={8}>
        <Flex
          height="100%"
          flexDirection="column"
          width="100%"
          gap="16px"
          style={{ ...subCardStyle, padding: "16px" }}
        >
          {/* The ring carries its own box now — see PieChart. */}
          <Flex flexDirection="column" width="100%">
            <PieChart
              legend={false}
              data={basket.map((item) => {
                return {
                  key: item.symbol,
                  value: Number((item.weight * 100).toFixed(0)),
                };
              })}
            />
          </Flex>
          <Flex flexDirection="column" width="100%" gap="8px">
            {/* The rule stated once, in the hint's place, rather than as a
                red asterisk and a parenthetical appended to the label. A
                requirement is not an error until it has been broken, and it
                had been shouting before the reader had typed anything. */}
            <Styled.FieldLabel>
              Name <i>at least 5 characters</i>
            </Styled.FieldLabel>
            <Styled.InputText
              maxLength={90}
              placeholder="Your Smartfolio Name"
              value={useSelector(selectName)}
              onChange={(e) => dispatch(setName(e.target.value))}
            />
          </Flex>
          <Flex flexDirection="column" width="100%" gap="8px">
            <Styled.FieldLabel>
              Description <i>at least 5 characters</i>
            </Styled.FieldLabel>
            <Styled.InputTextArea
              rows={4}
              placeholder="Your Smartfolio Description"
              maxLength={225}
              value={useSelector(selectDescription)}
              onChange={(e) => dispatch(setDescription(e.target.value))}
            />
          </Flex>
        </Flex>
      </Col>
      <Col span={24} lg={16}>
        <Flex
          flexDirection="column"
          width="100%"
          style={{ ...subCardStyle, padding: "20px", overflowX: "scroll" }}
        >
          <Styled.SectionName>Securities</Styled.SectionName>
          <Styled.Row>
            <Styled.ColumnName style={{ marginLeft: "14px" }}>
              Security
            </Styled.ColumnName>
            <Styled.ColumnName>Weight</Styled.ColumnName>
            <Styled.ColumnName>Min</Styled.ColumnName>
            <Styled.ColumnName>Max</Styled.ColumnName>
            <Styled.ColumnName />
            {basket.map(
              (
                {
                  symbol,
                  name,
                  weight,
                  minimum_bound_weight,
                  maximum_bound_weight,
                },
                index,
              ) => {
                return (
                  <>
                    {/* Ticker over name: one identity, one cell. */}
                    <Styled.Identity>
                      <Badge color={PieColors[index]} />
                      <Styled.Select
                        disabled={symbol === "CASH"}
                        value={symbol}
                        style={{ width: 100 }}
                        onChange={(value) => {
                          const selectedTicker = tickers.find(
                            (item) => item.symbol === value,
                          );
                          dispatch(
                            updateBasketInfo({
                              index,
                              security: selectedTicker,
                            }),
                          );
                        }}
                        options={tickers
                          .map(({ symbol }) => ({
                            value: symbol,
                            label: symbol,
                          }))
                          .slice(1)
                          .filter((item) => {
                            const selectedSymbols = basket.map(
                              (basketItem) => basketItem.symbol,
                            );
                            return !selectedSymbols.includes(item.value);
                          })}
                      />
                      {name ? (
                        <Styled.SecurityName>{name}</Styled.SecurityName>
                      ) : null}
                    </Styled.Identity>
                    <Styled.InputNum
                      disabled={symbol === "CASH"}
                      id="Weight"
                      value={(weight * 100).toFixed(0)}
                      min={0}
                      max={(+cashWeight + weight) * 100}
                      formatter={(value) => `${value}%`}
                      parser={(value) => value!.replace("%", "")}
                      onChange={(e) =>
                        dispatch(
                          updateBasketItem({
                            index,
                            attribute: "weight",
                            value: e,
                          }),
                        )
                      }
                    />
                    <Styled.InputNum
                      id="Min Weights"
                      value={(minimum_bound_weight * 100).toFixed(0)}
                      min={0}
                      max={(weight * 1000) / 10}
                      formatter={(value) => `${value}%`}
                      parser={(value) => value!.replace("%", "")}
                      onChange={(e) =>
                        dispatch(
                          updateBasketItem({
                            index,
                            attribute: "minimum_bound_weight",
                            value: e,
                          }),
                        )
                      }
                    />
                    <Styled.InputNum
                      id="Max Weights"
                      value={(maximum_bound_weight * 100).toFixed(0)}
                      min={(weight * 1000) / 10}
                      max={100}
                      formatter={(value) => `${value}%`}
                      parser={(value) => value!.replace("%", "")}
                      onChange={(e) =>
                        dispatch(
                          updateBasketItem({
                            index,
                            attribute: "maximum_bound_weight",
                            value: e,
                          }),
                        )
                      }
                    />
                    {/* A real button, not a glyph with an onClick. It was a
                        bare icon in a hard-coded #FF0000 — a colour from no
                        palette, at 60% opacity, with no hit area beyond the
                        drawing itself and nothing to say it was pressable
                        until you happened to be over it. Cash cannot be
                        removed, so on that row the control is DISABLED
                        rather than silently inert. */}
                    <UIButton
                      variant="ghost"
                      size="iconXs"
                      disabled={symbol === "CASH"}
                      aria-label={
                        symbol === "CASH"
                          ? "Cash cannot be removed"
                          : `Remove ${symbol}`
                      }
                      title={
                        symbol === "CASH"
                          ? "Cash cannot be removed"
                          : `Remove ${symbol}`
                      }
                      onClick={() => dispatch(removeBasketItem(index))}
                    >
                      <HugeIcon icon={CancelCircleIcon} size={15} />
                    </UIButton>
                  </>
                );
              },
            )}
            {/* The product's button. It was a row of text and an icon that
                happened to carry an onClick — nothing about it said it could
                be pressed except the cursor, and it sat in the grid as though
                it were another column heading. */}
            <UIButton
              variant="secondary"
              size="sm"
              onClick={() => dispatch(addBasketItem())}
              style={{ justifySelf: "start", whiteSpace: "nowrap" }}
            >
              <HugeIcon icon={PlusSignCircleIcon} size={14} />
              Add a security
            </UIButton>
          </Styled.Row>

          {/**
           * The sum, and what is still missing.
           *
           * Every security arrives at 0% and cash holds the whole 100%, and
           * the Build button is disabled until each one carries a weight, a
           * name of five characters and a description of five — none of
           * which was said anywhere. A disabled button with no reason is a
           * dead end: the reader can see it is not available and cannot see
           * what to do about it. This lists exactly what is outstanding, and
           * disappears when nothing is.
           */}
          <Styled.TotalRow>
            <Styled.ColumnName style={{ marginLeft: "14px" }}>
              Total
            </Styled.ColumnName>
            <span />
            <Styled.Total $ok={Math.round(weightsTotal) === 100}>
              {Math.round(weightsTotal)}%
            </Styled.Total>
          </Styled.TotalRow>
          {outstanding.length > 0 ? (
            <Styled.Note>
              Before you can build: {outstanding.join(", ")}.
            </Styled.Note>
          ) : null}
        </Flex>
      </Col>
    </Row>
  );
};
